import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip } from 'lucide-react';

const FileUploadButton = ({ modelInfo, onFileSelected, toast }) => {
    const fileInputRef = useRef(null);

    const getAcceptAttribute = () => {
        const modalities = modelInfo?.architecture?.input_modalities || [];
        const hasImage = modalities.includes('image');
        const hasFile = modalities.includes('file');
        if (hasImage && hasFile) {
            return "image/*,application/pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.ppt,.pptx";
        } else if (hasImage) {
            return "image/*";
        } else if (hasFile) {
            return "application/pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.ppt,.pptx";
        }
        return "";
    };

    const handleFileChange = (e: any) => {
        const file = e.target.files[0];
        if (!file) return;
        const maxSizeInBytes = 35 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
            toast({
                title: "File is too large",
                description: `File too large! Maximum size is 35MB. Your file is ${fileSizeMB}MB`,
                variant: "destructive",
            });
            e.target.value = '';
            return;
        }

        const modalities = modelInfo?.architecture?.input_modalities || [];
        const hasImage = modalities.includes('image');
        const hasFile = modalities.includes('file');

        const allowedImageTypes = [
            'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
            'image/webp', 'image/bmp', 'image/svg+xml'
        ];
        const allowedFileTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'text/csv',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/json',
            'text/html',
            'application/xml'
        ];

        const allowedFileExtensions = [
            '.pdf', '.doc', '.docx', '.txt', '.csv',
            '.xls', '.xlsx', '.ppt', '.pptx',
            '.json', '.html', '.xml'
        ];

        const getFileExtension = (filename: any) => {
            return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
        };

        const fileExtension = '.' + getFileExtension(file.name);
        const fileType = file.type;
        const isImage = fileType.startsWith('image/') || allowedImageTypes.includes(fileType);

        let isValidFile = false;
        let allowedTypes = [];
        if (hasImage && hasFile) {
            isValidFile =
                allowedImageTypes.includes(fileType) ||
                allowedFileTypes.includes(fileType) ||
                allowedFileExtensions.includes(fileExtension);
            allowedTypes = ['images', 'documents'];
        } else if (hasImage && !hasFile) {
            isValidFile = allowedImageTypes.includes(fileType);
            allowedTypes = ['images only (PNG, JPG, GIF, WebP)'];
        } else if (hasFile && !hasImage) {
            isValidFile =
                allowedFileTypes.includes(fileType) ||
                (allowedFileExtensions.includes(fileExtension) && !isImage);
            allowedTypes = ['documents only (PDF, DOC, TXT, etc.)'];
        } else {
            toast({
                title: "Not Supported",
                description: `This model does not support file uploads.`,
                variant: "destructive",
            });
            e.target.value = '';
            return;
        }

        if (!isValidFile) {
            toast({
                title: "File Type Not Supported",
                description: `This file type is not supported. Please upload ${allowedTypes.join(' or ')}. You selected: ${fileType || 'unknown type'} (${fileExtension})`,
                variant: "destructive",
            });
            e.target.value = '';
            return;
        }

        if (hasFile && !hasImage && isImage) {
            toast({
                title: "Images Not Supported",
                description: `Images are not supported. Please upload a document.`,
                variant: "destructive",
            });
            e.target.value = '';
            return;
        }

        if (onFileSelected) {
            onFileSelected(file);
            toast({
                title: "File Selected",
                description: `"${file.name}" selected (${(file.size / 1024).toFixed(0)}KB)`,
            });
        }

        e.target.value = '';
    };

    const shouldShowButton =
        modelInfo?.architecture?.input_modalities?.includes('image') ||
        modelInfo?.architecture?.input_modalities?.includes('file');

    if (!shouldShowButton) return null;

    return (
        <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            data-testid="button-attach"
            onClick={() => fileInputRef.current?.click()}
            type="button"
        >
            <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptAttribute()}
                onChange={handleFileChange}
                className="hidden"
            />
            <Paperclip className="h-4 w-4" />
        </Button>
    );
};

export default FileUploadButton;